import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, Calendar, Users, CheckCircle2, Clock, AlertTriangle, Bell } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { db } from '../../../lib/db';
import { useAppraisals } from '../../../hooks/useDatabase';
import { formatDate, getDaysUntil, isOverdue } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor } from '../../../lib/calculations';
import { toast } from 'sonner';
import AIAnalysisSummary from '../../shared/AIAnalysisSummary';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { useNotifications } from '../../../hooks/useDatabase';

export default function AppraisalsPage() {
  const { createAppraisal, loading } = useAppraisals();
  const navigate = useNavigate();
  const { createNotification } = useNotifications();
  const [appraisals, setAppraisals] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [assignData, setAssignData] = useState({ templateId: '', dueDate: '', cycleDays: 'none', autoReminders: false, reminderLeadDays: 'none' });
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [directOnly, setDirectOnly] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!appraisals.length || !users.length) return;
    const checkAndSendReminders = async () => {
      const now = Date.now();
      const pendingReminders = appraisals.filter(a => {
        if (!a.autoReminders) return false;
        if (a.remindersSent) return false;
        const lead = typeof a.reminderLeadDays === 'number' ? a.reminderLeadDays : Number(a.reminderLeadDays || 0);
        if (!lead) return false;
        if (!a.dueDate) return false;
        const msUntilDue = a.dueDate - now;
        return msUntilDue > 0 && msUntilDue <= lead * 24 * 60 * 60 * 1000;
      });

      if (pendingReminders.length === 0) return;

      for (const appraisal of pendingReminders) {
        const employee = getUserById(appraisal.userId);
        const targets = [appraisal.userId];
        if (employee?.supervisorId) targets.push(employee.supervisorId);

        try {
          await Promise.all(targets.map(userId => createNotification({
            userId,
            type: 'appraisal_reminder',
            title: 'Appraisal due soon',
            message: `${appraisal.title} is due on ${formatDate(appraisal.dueDate)}. Please complete it.`
          })));
          await db.appraisals.update(appraisal.id, { remindersSent: true });
          setAppraisals(prev => prev.map(a => a.id === appraisal.id ? { ...a, remindersSent: true } : a));
        } catch (error) {
          console.error('Failed to send reminder', error);
        }
      }
    };

    checkAndSendReminders();
    const interval = setInterval(checkAndSendReminders, 60 * 1000);
    return () => clearInterval(interval);
  }, [appraisals, users, createNotification]);

  const loadData = async () => {
    const [appraisalsData, usersData, templatesData] = await Promise.all([
      db.appraisals.toArray(),
      db.users.toArray(),
      db.templates.toArray()
    ]);
    setAppraisals(appraisalsData);
    setUsers(usersData);
    setTemplates(templatesData);
  };

  const getUserById = (id) => users.find(u => u.id === id);
  const employees = users.filter(u => u.role !== 'admin');

  const departments = [...new Set(employees.map(e => e.profile?.department).filter(Boolean))];

  const filteredSelectableEmployees = employees.filter(emp => {
    const matchesSearch = employeeSearch === '' ||
      emp.profile?.firstName?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
      emp.profile?.lastName?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
      emp.email?.toLowerCase().includes(employeeSearch.toLowerCase());
    const matchesRole = directOnly ? emp.role === 'employee' : (roleFilter === 'all' || emp.role === roleFilter);
    const matchesDept = departmentFilter === 'all' || emp.profile?.department === departmentFilter;
    return matchesSearch && matchesRole && matchesDept;
  });

  const filteredAppraisals = appraisals.filter(appraisal => {
    const user = getUserById(appraisal.userId);
    const matchesSearch = searchQuery === '' ||
      user?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      appraisal.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || appraisal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: appraisals.length,
    pending: appraisals.filter(a => a.status === 'employee_completed').length,
    inProgress: appraisals.filter(a => a.status === 'ready_to_evaluate').length,
    completed: appraisals.filter(a => a.status === 'finalized').length,
    overdue: appraisals.filter(a => isOverdue(a.dueDate) && a.status !== 'finalized').length
  };

  const toggleEmployee = (id) => {
    setSelectedEmployees(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  };

  const notifyAssignees = async (appraisal) => {
    try {
      const targets = [appraisal.userId, appraisal.managerId].filter(Boolean);
      await Promise.all(targets.map(userId => createNotification({
        userId,
        type: 'appraisal',
        title: 'Appraisal Update',
        message: `${appraisal.title} has an update awaiting your attention.`,
        link: userId === appraisal.userId ? `/employee/appraisals/${appraisal.id}` : `/supervisor/appraisals/${appraisal.id}`
      })));
      toast.success('Notification sent');
    } catch (error) {
      toast.error('Failed to send notification');
    }
  };

  const selectAllFilteredEmployees = () => {
    const filteredIds = filteredSelectableEmployees.map(emp => emp.id);
    setSelectedEmployees(prev => {
      const next = new Set(prev);
      filteredIds.forEach(id => next.add(id));
      return Array.from(next);
    });
  };

  const handleAssignAppraisal = async () => {
    if (!assignData.templateId || !assignData.dueDate || selectedEmployees.length === 0) {
      toast.error('Please select template, due date, and at least one employee');
      return;
    }

    const template = templates.find(t => t.id === assignData.templateId);
    const performanceAreas = template?.sections?.[0]?.competencies?.map(c => ({
      competencyId: c.id,
      competencyName: c.name,
      weight: c.weight,
      completedByEmployee: false,
      completedByManager: false
    })) || [];

    try {
      for (const empId of selectedEmployees) {
        const emp = getUserById(empId);

        await createAppraisal({
          templateId: assignData.templateId,
          userId: empId,
          managerId: emp?.supervisorId,
          title: `${template?.name || 'Performance Review'} - ${emp?.profile?.firstName} ${emp?.profile?.lastName}`,
          type: template?.type || 'annual',
          dueDate: new Date(assignData.dueDate).getTime(),
          performanceAreas,
          cycleDays: assignData.cycleDays && assignData.cycleDays !== 'none' ? Number(assignData.cycleDays) : null,
          autoReminders: assignData.autoReminders,
          reminderLeadDays: assignData.autoReminders && assignData.reminderLeadDays && assignData.reminderLeadDays !== 'none'
            ? Number(assignData.reminderLeadDays)
            : null,
          remindersSent: false,
          visibleToSupervisor: directOnly ? false : true,
          status: 'ready_to_evaluate',
          finalizedAt: null,
          managerSubmittedAt: null
        });
      }
      toast.success(`Appraisal assigned to ${selectedEmployees.length} employee(s)`);
      setShowAssignModal(false);
      setSelectedEmployees([]);
      setAssignData({ templateId: '', dueDate: '', cycleDays: 'none', autoReminders: false, reminderLeadDays: 'none' });
      loadData();
    } catch (error) {
      toast.error('Failed to assign appraisal');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Appraisals</h1>
          <p className="text-gray-500 mt-1">Manage and track all performance appraisals</p>
        </div>
        <Button onClick={() => setShowAssignModal(true)} className="bg-teal-600 hover:bg-teal-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Assign Appraisal
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.total}</p><p className="text-xs text-gray-500">Total</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.pending}</p><p className="text-xs text-gray-500">Pending Review</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center"><Calendar className="w-5 h-5 text-violet-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p><p className="text-xs text-gray-500">In Progress</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.completed}</p><p className="text-xs text-gray-500">Completed</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-50 rounded-lg flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{stats.overdue}</p><p className="text-xs text-gray-500">Overdue</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="ready_to_evaluate">Ready to Evaluate</SelectItem>
                <SelectItem value="employee_completed">Pending Review</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Employee</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Appraisal</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Progress</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Due Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppraisals.map(appraisal => {
                  const user = getUserById(appraisal.userId);
                  const overdue = isOverdue(appraisal.dueDate) && appraisal.status !== 'finalized';
                  return (
                    <React.Fragment key={appraisal.id}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {user?.profile?.firstName?.[0]}{user?.profile?.lastName?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900">{user?.profile?.firstName} {user?.profile?.lastName}</p>
                            <p className="text-xs text-gray-500">{user?.profile?.department}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{appraisal.title}</td>
                      <td className="py-3 px-4"><Badge className={getStatusBadgeColor(appraisal.status)}>{formatStatus(appraisal.status)}</Badge></td>
                      <td className="py-3 px-4">
                        <div className="w-24">
                          <Progress value={appraisal.progress?.overallPercent || 0} className="h-2" />
                          <span className="text-xs text-gray-500">{appraisal.progress?.overallPercent || 0}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-sm ${overdue ? 'text-rose-600 font-medium' : 'text-gray-600'}`}>
                          {formatDate(appraisal.dueDate)}
                          {overdue && <span className="block text-xs">Overdue</span>}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          {appraisal.status !== 'finalized' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => notifyAssignees(appraisal)}
                            >
                              <Bell className="w-4 h-4 mr-1" /> Notify
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/appraisals/${appraisal.id}`)}
                          >
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="6" className="px-4 pb-4 pt-0 border-b border-gray-50">
                        <AIAnalysisSummary appraisal={appraisal} />
                      </td>
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredAppraisals.length === 0 && <div className="text-center py-8 text-gray-500">No appraisals found</div>}
        </CardContent>
      </Card>

      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Appraisal</DialogTitle>
            <DialogDescription>Select employees and template to assign a new appraisal</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template *</Label>
              <Select value={assignData.templateId} onValueChange={(v) => setAssignData({...assignData, templateId: v})}>
                <SelectTrigger><SelectValue placeholder="Select template..." /></SelectTrigger>
                <SelectContent>
                  {templates.map(template => (<SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Input type="date" value={assignData.dueDate} onChange={(e) => setAssignData({...assignData, dueDate: e.target.value})} />
            </div>
            <div className="flex flex-col md:flex-row gap-3 items-start">
              <Button
                type="button"
                variant={directOnly ? 'default' : 'outline'}
                className={directOnly ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                onClick={() => setDirectOnly(!directOnly)}
              >
                Direct (employees only)
              </Button>
              <p className="text-xs text-gray-500">Perfect one-off evaluations.</p>
            </div>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  {/* Cycle */}
  <div className="space-y-2">
    <div className="h-6 flex items-center">
      <Label>Cycle (optional)</Label>
    </div>

    <Select
      value={assignData.cycleDays}
      onValueChange={(v) =>
        setAssignData({ ...assignData, cycleDays: v })
      }
    >
      <SelectTrigger>
        <SelectValue placeholder="No cycle" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No cycle</SelectItem>
        <SelectItem value="7">Every 7 days</SelectItem>
        <SelectItem value="15">Every 15 days</SelectItem>
        <SelectItem value="30">Every 30 days</SelectItem>
      </SelectContent>
    </Select>
  </div>

  {/* Auto Reminders */}
  <div className="space-y-2">
    <div className="h-6 flex items-center gap-2">
      <Checkbox
        checked={assignData.autoReminders}
        onCheckedChange={(checked) =>
          setAssignData({
            ...assignData,
            autoReminders: !!checked,
            reminderLeadDays: !!checked
              ? assignData.reminderLeadDays
              : "none",
          })
        }
      />
      <Label>Enable auto reminders</Label>
    </div>

    <Select
      disabled={!assignData.autoReminders}
      value={assignData.reminderLeadDays}
      onValueChange={(v) =>
        setAssignData({ ...assignData, reminderLeadDays: v })
      }
    >
      <SelectTrigger>
        <SelectValue placeholder="Select timing" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Select timing</SelectItem>
        <SelectItem value="1">1 day before due</SelectItem>
        <SelectItem value="3">3 days before due</SelectItem>
        <SelectItem value="7">7 days before due</SelectItem>
      </SelectContent>
    </Select>
  </div>
</div>

            <div className="space-y-2">
              <Label>Select Employees *</Label>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search employees by name or email"
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="md:w-40"><SelectValue placeholder="Role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="md:w-44"><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(dept => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    className="md:w-40"
                    onClick={selectAllFilteredEmployees}
                    disabled={filteredSelectableEmployees.length === 0}
                  >
                    Select All Filtered
                  </Button>
                </div>

                <div className="border rounded-lg max-h-52 overflow-y-auto">
                  {filteredSelectableEmployees.map(emp => (
                    <div key={emp.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                      <Checkbox checked={selectedEmployees.includes(emp.id)} onCheckedChange={() => toggleEmployee(emp.id)} />
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium">
                        {emp.profile?.firstName?.[0]}{emp.profile?.lastName?.[0]}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{emp.profile?.firstName} {emp.profile?.lastName}</p>
                        <p className="text-xs text-gray-500">{emp.profile?.department} • {emp.role}</p>
                      </div>
                    </div>
                  ))}
                  {filteredSelectableEmployees.length === 0 && (
                    <div className="p-3 text-sm text-gray-500 text-center">No users match the current filters</div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">{selectedEmployees.length} employee(s) selected</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" disabled={loading || selectedEmployees.length === 0} onClick={handleAssignAppraisal}>
              {loading ? 'Assigning...' : 'Assign Appraisal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
